/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  Grid,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Typography,
  Skeleton,
  TextField,
  Button,
  FormHelperText,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";

import CustomRightImage from "@components/CustomRightImagev2";
import useStatusConfig from "@hooks/useStatusConfig";
import * as Sentry from "@sentry/react";
import {
  useLazyGetLoanCreditUseQuery,
  useLazyPostLoanUseQuery,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { ButtonTypeSelectedCustom } from "@components/ButtonTypeSelectedCustom";
import icon_check from "@assets/img/icon_check_cotizador.svg";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  ApiError,
  ItemLabelValue,
  LoanCreditUseStructure,
  PlatformEstructure,
  WorkTypeEstructure,
} from "@interfaces/index";
import {
  useLazyGetLoanUseCategoriesConfigQuery,
  useLazyGetPlatformConfigQuery,
  useLazyGetWorkTypeConfigQuery,
} from "@api/catalogs";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setLoanStatus } from "@store/slices/registerSlice";
import useTofuSteps from "@hooks/useTofuSteps";
import { ProgressBar } from "@components/ProgressBar";
import { InactivityHandler } from "@components/InactivityHandler";

const SCREEN_STATUS = "CUSMO";

const UsoDeMoto = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isShortScreen = useMediaQuery("(max-height:820px)");
  const dispatch = useAppDispatch();
  const applicationData = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [triggerSaveUseSelected] = useLazyPostLoanUseQuery();
  const [getLoanCreditUse] = useLazyGetLoanCreditUseQuery();

  const [getLoanUseCategoriesConfig] = useLazyGetLoanUseCategoriesConfigQuery();
  const [getWorkTypeConfig] = useLazyGetWorkTypeConfigQuery();
  const [getPlatformConfig] = useLazyGetPlatformConfigQuery();

  const [creditUseList, setCreditUseList] = useState<ItemLabelValue[]>([]);
  const [workTypeList, setWorkTypeList] = useState<WorkTypeEstructure[]>([]);
  const [platformList, setPlatformList] = useState<PlatformEstructure[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const [isLoadingForm, setIsLoadingForm] = useState(false);

  const formik = useFormik({
    initialValues: {
      resource_use_parent_id: "",
      reason: "",
      work_type_id: "",
      platform_id: "",
    },
    validationSchema: yup.object().shape({
      resource_use_parent_id: yup.string().required("Seleccion requerida"),
      reason: yup
        .string()
        .trim()
        .max(150, "Máximo 150 caracteres")
        .test("reason-required", "Campo requerido", (value) => {
          if (!showReason) return true;
          return Boolean(value && value.trim().length > 0);
        }),

      work_type_id: yup.string().required("Seleccion requerida"),
      platform_id: yup.string().test("platform-required", "Seleccion requerida", (value) => {
        if (platformList.length === 0) return true;
        return Boolean(value && value.trim().length > 0);
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!formik.isValid) {
          return;
        }

        if (!nextStatus) {
          throw new Error("No se encontró el siguiente estado");
        }

        setSubmitting(true);

        const body = {
          resource_use_parent_id: values.resource_use_parent_id,
          work_type_id: values.work_type_id,
          platform_id: values.platform_id || null,
          reason: values.reason,
        };

        await triggerSaveUseSelected({
          loanId: applicationData.id,
          body: body,
        }).unwrap();

        await triggerPostNextStage({
          applicationId: applicationData.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        dispatch(setLoanStatus(nextStatus.code));
        navigate(nextStatus.url);
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "UsoDeCredito",
            values: values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const getCurrentLoanCreditUse = async (): Promise<LoanCreditUseStructure | null> => {
    try {
      return await getLoanCreditUse(applicationData.id).unwrap();
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [list_credit_use, response_loan_credit_use] = await Promise.all([
          getLoanUseCategoriesConfig({ taxCode: "CVMPF" }).unwrap(),
          getCurrentLoanCreditUse(),
        ]);

        const list_credit_use_mapped =
          list_credit_use?.map((item) => ({
            value: item.loan_use_categories_id,
            label: item.loan_use_categories.name,
            is_other: item.loan_use_categories.other,
            order: item.loan_use_categories.order,
          })) || [];

        list_credit_use_mapped.sort((a, b) => a.order - b.order);

        setCreditUseList(list_credit_use_mapped || []);

        if (response_loan_credit_use && response_loan_credit_use.id) {
          formik.setValues({
            resource_use_parent_id: response_loan_credit_use.resource_use_parent_id || response_loan_credit_use.id,
            reason: response_loan_credit_use.reason || "",
            work_type_id: response_loan_credit_use.work_type_id || "",
            platform_id: response_loan_credit_use.platform_id || "",
          });

          setTimeout(() => {
            formik.validateForm();
          }, 200);
        } else if (!formik.values.resource_use_parent_id && list_credit_use_mapped.length > 0) {
          // Si no existe selección previa, preselecciona la primera opción disponible.

          setTimeout(() => {
            formik.setValues({
              resource_use_parent_id: list_credit_use_mapped[0].value,
              reason: "",
              work_type_id: "",
              platform_id: "",
            });
            formik.validateForm();
          }, 200);
        }
      } catch (error) {
        console.log("Ha ocurrido un error al obtener los datos", error);
        Sentry.captureException(error, {
          extra: {
            component: "UsoDeCredito",
            values: {},
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!formik.values.resource_use_parent_id) {
      setWorkTypeList([]);
      setPlatformList([]);
      return;
    }
    handleWorkTypeChange(formik.values.resource_use_parent_id);
  }, [formik.values.resource_use_parent_id]);

  useEffect(() => {
    const selectedWorkType = workTypeList.find((item) => item.work_type_id === formik.values.work_type_id);
    const selectedPlatform = platformList.find((item) => item.platform_id === formik.values.platform_id);
    const workTypeDescriptor = [
      selectedWorkType?.work_type.code,
      selectedWorkType?.work_type.name,
      selectedWorkType?.work_type.description,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const platformDescriptor = [
      selectedPlatform?.platform.code,
      selectedPlatform?.platform.name,
      selectedPlatform?.platform.description,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    setShowReason(
      workTypeDescriptor.includes("otro") ||
        workTypeDescriptor.includes("other") ||
        platformDescriptor.includes("otra") ||
        platformDescriptor.includes("otro") ||
        platformDescriptor.includes("other"),
    );
  }, [formik.values.work_type_id, formik.values.platform_id, workTypeList, platformList]);

  const handleWorkTypeChange = async (value: string) => {
    try {
      setIsLoadingForm(true);
      const response = await getWorkTypeConfig({ taxcode: "CVMPF", loan_use_categories_id: value }).unwrap();
      setWorkTypeList(response);
      setPlatformList([]);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "UsoDeMoto",
          values: {},
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoadingForm(false);
    }
  };

  const handlePlataformChange = async (value: string) => {
    try {
      setIsLoadingForm(true);
      const response = await getPlatformConfig({ taxcode: "CVMPF", work_type_id: value }).unwrap();
      setPlatformList(response);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "UsoDeMoto",
          values: {},
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoadingForm(false);
    }
  };

  useEffect(() => {
    if (formik.values.work_type_id) {
      handlePlataformChange(formik.values.work_type_id);
    } else {
      setPlatformList([]);
    }
  }, [formik.values.work_type_id]);

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8} mt={2}>
          <InactivityHandler />
          <Box
            sx={{
              mt: 2,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant={isMobileOrTablet ? "h4" : "h3"}
              fontWeight={500}
              textAlign="center"
              color={theme?.palette?.primary?.dark}
            >
              Uso del la motocicleta
            </Typography>
          </Box>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3, mb: 4 }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Box display="flex" flexDirection="row" gap={2} justifyContent="center">
                {!isLoading && !formik.isSubmitting ? (
                  <>
                    {creditUseList.length > 0 ? (
                      creditUseList.map((item) => (
                        <ButtonTypeSelectedCustom
                          key={item.value}
                          title={item.label}
                          onClick={async () => {
                            await formik.setFieldValue("resource_use_parent_id", item.value);
                            await formik.setFieldValue("work_type_id", "");
                            await formik.setFieldValue("platform_id", "");
                            await formik.setFieldValue("reason", "");
                            formik.setFieldTouched("work_type_id", false, false);
                            formik.setFieldTouched("platform_id", false, false);
                            formik.setFieldTouched("reason", false, false);
                          }}
                          isSelected={formik.values.resource_use_parent_id === item.value}
                          isMovile={isMobileOrTablet}
                          height={isShortScreen ? "60px" : "70px"}
                          heightMoviles="60px"
                          width={isShortScreen ? "170px" : "190px"}
                          widthMoviles="98px"
                          icon={icon_check}
                          width_icon="22px"
                          height_icon="22px"
                          font_size={isShortScreen ? "12px" : "14px"}
                          top_icon="-12px"
                          font_size_movil="11px"
                        />
                      ))
                    ) : (
                      <Typography variant="body1" mt={2} color={theme.palette.primary.dark}>
                        No se encontraron opciones disponibles.
                      </Typography>
                    )}
                  </>
                ) : (
                  <Box mt={1} width="100%">
                    <Skeleton variant="text" animation={"wave"} height="45px" />
                  </Box>
                )}
              </Box>
              <Box display="flex" flexDirection="column" alignItems="center">
                {formik.touched.resource_use_parent_id && formik.errors.resource_use_parent_id && (
                  <FormHelperText>{formik.errors.resource_use_parent_id}</FormHelperText>
                )}
              </Box>

              {!isLoading ? (
                <>
                  <FormControl
                    fullWidth
                    required
                    sx={{ mt: 2 }}
                    error={formik.touched.work_type_id && Boolean(formik.errors.work_type_id)}
                  >
                    <InputLabel id="work_type_id-label">Tipo de trabajo</InputLabel>
                    <Select
                      labelId="work_type_id-label"
                      id="work_type_id"
                      name="work_type_id"
                      fullWidth
                      required
                      label="Tipo de trabajo"
                      value={formik.values.work_type_id}
                      onBlur={() => {
                        formik.setFieldTouched("work_type_id", true, true);
                      }}
                      onChange={async (event) => {
                        await formik.setFieldValue("work_type_id", event.target.value, true);
                        await formik.setFieldValue("platform_id", "", false);
                        formik.setFieldTouched("work_type_id", true, false);
                        formik.setFieldTouched("platform_id", false, false);
                        await formik.validateField("work_type_id");
                      }}
                      disabled={formik.isSubmitting || isLoadingForm}
                    >
                      <MenuItem value="">Selecciona</MenuItem>
                      {workTypeList.length > 0 ? (
                        workTypeList.map((item: WorkTypeEstructure) => (
                          <MenuItem key={item.id} value={item.work_type_id}>
                            {item.work_type.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>
                          No hay opciones disponibles
                        </MenuItem>
                      )}
                    </Select>
                    {formik.touched.work_type_id && Boolean(formik.errors.work_type_id) && (
                      <FormHelperText>{formik.errors.work_type_id}</FormHelperText>
                    )}
                  </FormControl>

                  {platformList.length > 0 && (
                    <FormControl
                      fullWidth
                      required={platformList.length > 0}
                      sx={{ mt: 2 }}
                      error={formik.touched.platform_id && Boolean(formik.errors.platform_id)}
                    >
                      <InputLabel id="platform_id-label">Plataforma</InputLabel>
                      <Select
                        labelId="platform_id-label"
                        id="platform_id"
                        name="platform_id"
                        fullWidth
                        required={platformList.length > 0}
                        label="Plataforma"
                        value={formik.values.platform_id}
                        onBlur={() => {
                          formik.setFieldTouched("platform_id", true, true);
                        }}
                        onChange={async (event) => {
                          await formik.setFieldValue("platform_id", event.target.value, true);
                          formik.setFieldTouched("platform_id", true, false);
                          await formik.validateField("platform_id");
                        }}
                        disabled={formik.isSubmitting || isLoadingForm}
                      >
                        <MenuItem value="">Selecciona</MenuItem>
                        {platformList.length > 0 ? (
                          platformList.map((item: PlatformEstructure) => (
                            <MenuItem key={item.id} value={item.platform_id}>
                              {item.platform.name}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="" disabled>
                            No hay opciones disponibles
                          </MenuItem>
                        )}
                      </Select>
                      {formik.touched.platform_id && Boolean(formik.errors.platform_id) && (
                        <FormHelperText>{formik.errors.platform_id}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </>
              ) : (
                <Box mt={1} width="100%">
                  <Skeleton variant="text" animation={"wave"} height="45px" />
                  <Skeleton variant="text" animation={"wave"} height="45px" />
                </Box>
              )}

              {showReason && (
                <TextField
                  id="reason"
                  name="reason"
                  label="Describe brevemente el uso a darle al vehículo (máximo 150 caracteres)"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.reason}
                  error={formik.touched.reason && Boolean(formik.errors.reason)}
                  helperText={formik.touched.reason && formik.errors.reason}
                  disabled={isLoadingForm || formik.isSubmitting}
                  inputProps={{ maxLength: 150 }}
                  multiline
                  rows={4}
                />
              )}

              <Box display={"flex"} justifyContent={"center"} mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button
                  type="submit"
                  variant="default"
                  disabled={formik.isSubmitting || !formik.isValid || isLoadingForm}
                >
                  Continuar
                </Button>
              </Box>
            </form>
          </Container>
        </Grid>

        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default UsoDeMoto;
